import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#buildQuickAddDetails(productGrid);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    if (window.TDMVariantMediaFilter && typeof window.TDMVariantMediaFilter.refresh === 'function') {
      window.TDMVariantMediaFilter.refresh();
    }

    // Initialize TDM Fabric Calculator in the modal
    // @ts-ignore - TDMFabricCalculator is defined in quick-add-modal.liquid
    if (window['TDMFabricCalculator'] && typeof window['TDMFabricCalculator'].init === 'function') {
      // @ts-ignore
      window['TDMFabricCalculator'].init(modalContent);
    }

    // Initialize TDM Filling Option in the modal (for coussins)
    // @ts-ignore - TDMFilling is defined in tdm-filling-option.js
    if (window['TDMFilling'] && typeof window['TDMFilling'].init === 'function') {
      // @ts-ignore
      window['TDMFilling'].init();
    }
  }

  /**
   * Build a dedicated quick-add details layout (modal only).
   * @param {Element} productGrid - The product grid element
   */
  #buildQuickAddDetails(productGrid) {
    const details = productGrid.querySelector('.product-details');
    if (!details) return;

    const productUrl = this.productPageUrl || '';
    const titleSource =
      details.querySelector('.tdm-details__title') ||
      details.querySelector('.view-product-title a') ||
      details.querySelector('[data-product-title]') ||
      details.querySelector('h1, h2, h3');

    const titleText = (titleSource && titleSource.textContent ? titleSource.textContent.trim() : '') ||
      this.dataset.productTitle ||
      '';

    const titleLink = document.createElement('a');
    titleLink.className = 'quick-add-details__title';
    titleLink.textContent = titleText;
    titleLink.href = titleSource?.getAttribute?.('href') || productUrl || '#';

    const priceEl =
      details.querySelector('product-price') ||
      details.querySelector('[data-product-price]') ||
      details.querySelector('.tdm-details__current-price');
    const compareEl = details.querySelector('.tdm-details__compare-price');
    const unitEl = details.querySelector('.tdm-details__price-unit');
    const stockEl = details.querySelector('.tdm-details__stock') || details.querySelector('product-inventory');

    const variantPicker = details.querySelector('variant-picker') || details.querySelector('.tdm-details__variants');
    const fabricCalc = details.querySelector('.tdm-details__fabric-calc');
    const buyButtons =
      details.querySelector('product-form-component') ||
      details.querySelector('.buy-buttons-block') ||
      details.querySelector('.tdm-details__buy-buttons');

    const newDetails = document.createElement('div');
    newDetails.className = 'product-details quick-add-details';

    const inner = document.createElement('div');
    inner.className = 'quick-add-details__inner';
    newDetails.appendChild(inner);

    const header = document.createElement('div');
    header.className = 'quick-add-details__header';
    header.appendChild(titleLink);

    const priceRow = document.createElement('div');
    priceRow.className = 'quick-add-details__price-row';

    const priceWrap = document.createElement('div');
    priceWrap.className = 'quick-add-details__price';

    if (priceEl && priceEl.tagName && priceEl.tagName.toLowerCase() === 'product-price') {
      priceWrap.appendChild(priceEl);
    } else {
      if (compareEl) priceWrap.appendChild(compareEl);
      if (priceEl) priceWrap.appendChild(priceEl);
      if (unitEl) priceWrap.appendChild(unitEl);
    }

    priceRow.appendChild(priceWrap);
    if (stockEl) priceRow.appendChild(stockEl);

    header.appendChild(priceRow);
    inner.appendChild(header);

    if (variantPicker) {
      const options = document.createElement('div');
      options.className = 'quick-add-details__options';
      options.appendChild(variantPicker);
      inner.appendChild(options);
    }

    if (fabricCalc) {
      const calc = document.createElement('div');
      calc.className = 'quick-add-details__calc';
      calc.appendChild(fabricCalc);
      inner.appendChild(calc);
    }

    const fillingOption = details.querySelector('.tdm-filling-option');
    if (fillingOption) {
      const filling = document.createElement('div');
      filling.className = 'quick-add-details__filling';
      filling.appendChild(fillingOption);
      inner.appendChild(filling);
    }

    const actions = document.createElement('div');
    actions.className = 'quick-add-details__actions';
    if (buyButtons) actions.appendChild(buyButtons);
    inner.appendChild(actions);

    details.replaceWith(newDetails);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    // Tissus products always stay on "choose" (user must select fabric length)
    if (this.dataset.forceChoose === 'true') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const quickAddTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-details__title'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (quickAddTitle) quickAddTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
